import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "Components/DesignSystem/Toast/useToast";
// release-sept-29 merge (2026-09-15): react-toastify is no longer a dependency — the app has one
// toast system (design-system § 116). Same calls, same messages; they land in the DS ToastRegion.
import { AppContext } from "Context/AppContext";

import ModelConfigTopBar from "./ModelConfigTopBar";
import ModelReadOnlyBanner from "./ModelReadOnlyBanner";
import StrategyPicker from "./StrategyPicker";
import StrategyChangeWarningDialog from "./StrategyChangeWarningDialog";
import StrategyPreviewDrawer from "./StrategyPreviewDrawer";
import RenameModelDialog from "./RenameModelDialog";
import ModeTable from "./ModeTable";

import {
  getCommendModel,
  getCommendStrategies,
  previewStrategy,
  renameCommendModel,
  updateCommendModel,
} from "../Commend3Services/Commend3Services";
import {
  Commend3Model,
  Commend3PreviewProduct,
  Commend3Strategy,
  Commend3StrategyMode,
} from "../Types";
import {
  getInScopeModes,
  getOutOfScopeModes,
  getPrimarySubModel,
  getStrategyOptionsForModel,
  isModelLocked,
  isPromoted,
  isSubstitutionModel,
} from "../Commend3Helper";
import {
  AUTOSAVE_DEBOUNCE_MS,
  AUTOSAVE_SAVED_TOAST,
  COMMEND3_PATH,
  ENGINE_CONTROLLED_NOTE,
  IN_SCOPE_CHIP,
  IN_SCOPE_LABEL,
  IN_SCOPE_NOTE,
  OUT_OF_SCOPE_CHIP,
  OUT_OF_SCOPE_LABEL,
  OUT_OF_SCOPE_NOTE,
  PROMOTED_LOCK_TOAST,
  RECOMMENDER_READ,
} from "../Commend3Constants";

const generateSubModelUid = (): string =>
  Array.from({ length: 24 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

const ModelConfigPage = () => {
  const appData = useContext(AppContext);
  const navigate = useNavigate();
  const { modelId } = useParams<{ modelId: string }>();

  const [model, setModel] = useState<Commend3Model | null>(null);
  const [strategies, setStrategies] = useState<Commend3Strategy[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [pendingStrategy, setPendingStrategy] = useState<Commend3Strategy | null>(null);
  const [previewTarget, setPreviewTarget] = useState<Commend3Strategy | null>(null);
  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [renaming, setRenaming] = useState<boolean>(false);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<Commend3Model | null>(null);
  const mountedRef = useRef<boolean>(true);

  const channelKey = appData?.channelKey;
  const readPermission = appData.pm.checkPermission(RECOMMENDER_READ);

  const locked = isModelLocked(model);
  const subModel = model ? getPrimarySubModel(model) : null;
  const strategyOptions = getStrategyOptionsForModel(strategies, model);
  const substitutionOnly = isSubstitutionModel(model);

  useEffect(() => {
    if (!readPermission) {
      navigate("/workspace/module/permission/denied");
      return;
    }
    if (!channelKey || !modelId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [modelRes, strategyRes] = await Promise.all([
          getCommendModel(channelKey, modelId),
          getCommendStrategies(channelKey),
        ]);
        if (cancelled) return;
        if (modelRes?.done && modelRes?.data) {
          setModel(modelRes.data);
        } else {
          toast.error(modelRes?.message ?? "Failed to load model");
        }
        if (strategyRes?.done) setStrategies(strategyRes?.data ?? []);
      } catch (error) {
        console.error("Error loading Commend 3.0 model config", error);
        if (!cancelled) toast.error("Failed to load model. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [channelKey, modelId, readPermission, navigate]);

  const persist = useCallback(async (next: Commend3Model) => {
    try {
      const res = await updateCommendModel(next);
      if (res?.done) {
        if (mountedRef.current) setSavedAt(Date.now());
        toast.success(AUTOSAVE_SAVED_TOAST);
      } else {
        toast.error(res?.message ?? "Failed to save changes");
      }
    } catch (error) {
      console.error("Error saving Commend 3.0 model", error);
      toast.error("Failed to save changes. Please try again.");
    }
  }, []);

  const scheduleSave = useCallback(
    (next: Commend3Model) => {
      pendingSaveRef.current = next;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        const pending = pendingSaveRef.current;
        pendingSaveRef.current = null;
        if (pending) persist(pending);
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [persist]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        const pending = pendingSaveRef.current;
        pendingSaveRef.current = null;
        if (pending) persist(pending);
      }
    };
  }, [persist]);

  const applyStrategy = useCallback(
    (strategy: Commend3Strategy) => {
      if (!model) return;
      const current = getPrimarySubModel(model);
      const next: Commend3Model = {
        ...model,
        sub_models: [
          {
            uid: current?.uid || generateSubModelUid(),
            name: current?.name ?? "default submodel",
            strategy_id: strategy.uid,
            strategy_name: strategy.name,
            modes: JSON.parse(JSON.stringify(strategy.modes ?? {})),
          },
        ],
      };
      setModel(next);
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      pendingSaveRef.current = null;
      persist(next);
    },
    [model, persist]
  );

  const handleSelectStrategy = (strategy: Commend3Strategy) => {
    if (subModel?.strategy_id === strategy.uid) return;
    if (locked) {
      toast.error(PROMOTED_LOCK_TOAST);
      return;
    }
    if (!subModel?.strategy_id || substitutionOnly) {
      applyStrategy(strategy);
      return;
    }
    setPendingStrategy(strategy);
  };

  const handleWidgetTitleChange = (modeUid: string, value: string) => {
    if (!model || locked) return;
    const current = getPrimarySubModel(model);
    if (!current) return;

    const patch = (list: Commend3StrategyMode[] | undefined) =>
      (list ?? []).map((mode) =>
        mode.uid === modeUid ? { ...mode, widget_title: value } : mode
      );

    const nextModes = {
      ...(current.modes ?? {}),
      in_scope: patch(current.modes?.in_scope),
      ...(current.modes?.out_of_scope !== undefined
        ? { out_of_scope: patch(current.modes?.out_of_scope) }
        : {}),
      ...(current.modes?.out_scope !== undefined
        ? { out_scope: patch(current.modes?.out_scope) }
        : {}),
    };

    const next: Commend3Model = {
      ...model,
      sub_models: [{ ...current, modes: nextModes }],
    };
    setModel(next);
    scheduleSave(next);
  };

  const handleRename = async (name: string) => {
    if (!model) return;
    try {
      setRenaming(true);
      const res = await renameCommendModel(model, name);
      if (res?.done) {
        toast.success("Model renamed successfully");
        setModel({ ...model, name });
        setRenameOpen(false);
      } else {
        toast.error(res?.message ?? "Failed to rename model");
      }
    } catch (error) {
      console.error("Error renaming Commend 3.0 model", error);
      toast.error("Failed to rename model. Please try again.");
    } finally {
      setRenaming(false);
    }
  };

  const handlePreviewSearch = useCallback(
    async (term: string): Promise<Commend3PreviewProduct | null> => {
      if (!channelKey || !previewTarget) return null;
      try {
        const res = await previewStrategy(channelKey, previewTarget.uid, term);
        return res?.done ? res?.data ?? null : null;
      } catch (error) {
        console.error("Error previewing Commend 3.0 strategy", error);
        return null;
      }
    },
    [channelKey, previewTarget]
  );

  if (loading) {
    return (
      <div className="tw-px-6 tw-py-10 tw-text-[13px] tw-text-[#59596E]">Loading model…</div>
    );
  }

  if (!model) {
    return (
      <div className="tw-px-6 tw-py-10 tw-text-[13px] tw-text-[#59596E]">
        This model could not be loaded.
      </div>
    );
  }

  return (
    <div
      id="commend3-model-config"
      className="tw-flex tw-h-full tw-flex-col"
      style={{ background: "#FAFBFD" }}
    >
      <ModelConfigTopBar
        model={model}
        editable={!locked}
        saved={Boolean(savedAt)}
        onBack={() => navigate(COMMEND3_PATH)}
        onRename={() => setRenameOpen(true)}
      />

      <div className="tw-flex tw-flex-1 tw-overflow-hidden">
        <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-6 tw-pb-8 tw-pt-[22px]">
          {locked ? <ModelReadOnlyBanner model={model} /> : null}

          <div
            className="tw-flex tw-shrink-0 tw-flex-col tw-gap-[26px] tw-rounded-xl tw-bg-white tw-p-[22px]"
            style={{ border: "1px solid #E3E5EC" }}
          >
            <StrategyPicker
              strategies={strategyOptions}
              selectedStrategyId={subModel?.strategy_id ?? null}
              locked={locked}
              substitutionOnly={substitutionOnly}
              onSelect={handleSelectStrategy}
              onPreview={setPreviewTarget}
            />

            <ModeTable
              id="commend3-in-scope-modes"
              chip={IN_SCOPE_CHIP}
              label={IN_SCOPE_LABEL}
              note={IN_SCOPE_NOTE}
              inScope
              modes={getInScopeModes(subModel)}
              locked={locked}
              onWidgetTitleChange={handleWidgetTitleChange}
            />

            <ModeTable
              id="commend3-out-of-scope-modes"
              chip={OUT_OF_SCOPE_CHIP}
              label={OUT_OF_SCOPE_LABEL}
              note={OUT_OF_SCOPE_NOTE}
              inScope={false}
              modes={getOutOfScopeModes(subModel)}
              locked={locked}
              onWidgetTitleChange={handleWidgetTitleChange}
            />

            <div
              className="tw-text-[13.5px] tw-text-[#59596E]"
              style={{
                background: "#F4F5F9",
                padding: "12px 15px",
                borderLeft: "2.5px solid #c8c6bf",
                lineHeight: 1.6,
              }}
            >
              {ENGINE_CONTROLLED_NOTE}
            </div>
          </div>
        </div>
      </div>

      <StrategyChangeWarningDialog
        pendingStrategy={pendingStrategy}
        promoted={isPromoted(model)}
        onCancel={() => setPendingStrategy(null)}
        onConfirm={() => {
          if (pendingStrategy) applyStrategy(pendingStrategy);
          setPendingStrategy(null);
        }}
      />

      <StrategyPreviewDrawer
        strategy={previewTarget}
        onClose={() => setPreviewTarget(null)}
        onSearch={handlePreviewSearch}
      />

      <RenameModelDialog
        model={renameOpen ? model : null}
        saving={renaming}
        onClose={() => setRenameOpen(false)}
        onConfirm={handleRename}
      />
    </div>
  );
};

export default ModelConfigPage;
